/**
 * useShadowMode hook
 *
 * Provides shadow mode operations for support/superadmin users.
 * Uses useTypedApi for API calls and manages the shadow store.
 */
import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useShadowStore } from '@splitfiesta/core';

import { extractData } from '@/utils/apiTypes';

import { useTypedApi } from './useTypedApi';

interface ShadowTokenData {
  shadowToken: string;
  targetUser: {
    id: string;
    email: string;
    name: string;
    profileImage?: string;
  };
  expiresIn: number;
}

export function useShadowMode() {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const isActive = useShadowStore(s => s.isActive);
  const targetUser = useShadowStore(s => s.targetUser);
  const clearShadowStore = useShadowStore(s => s.endShadow);

  const enterShadowMode = useCallback(
    async (target: { userId?: string; email?: string }) => {
      const response = await api.admin.startShadow({
        targetUserId: target.userId,
        email: target.email,
      });
      const data = extractData<ShadowTokenData>(response);
      useShadowStore
        .getState()
        .startShadow(data.shadowToken, data.targetUser, data.expiresIn);
      // Reset all queries: clears cached data AND refetches active observers
      await queryClient.resetQueries();
    },
    [api, queryClient]
  );

  const exitShadowMode = useCallback(
    async (targetUserId?: string) => {
      const userId = targetUserId || targetUser?.id;
      if (userId) {
        await api.admin.endShadow({ targetUserId: userId });
      }
      clearShadowStore();
      // Reset all queries: clears cached data AND refetches active observers
      await queryClient.resetQueries();
    },
    [api, targetUser, clearShadowStore, queryClient]
  );

  const searchUsers = useCallback(
    async (query: string) => {
      return api.admin.searchUsers(query);
    },
    [api]
  );

  return {
    isActive,
    targetUser,
    enterShadowMode,
    exitShadowMode,
    searchUsers,
  };
}
